import type { ReactNode } from 'react';

import * as Sentry from '@sentry/react';

import { recoverFromChunkLoadError } from '@liscio/common';
import { APP_VERSION } from 'constants/constants';

type PDFViewerErrorBoundaryProps = {
  children: ReactNode;
};

const pdfViewerErrorFallbackStyle: React.CSSProperties = {
  alignItems: 'center',
  display: 'flex',
  height: '100%',
  justifyContent: 'center',
};

const pdfViewerErrorFallback = (
  <div role="alert" style={pdfViewerErrorFallbackStyle}>
    Unable to load PDF viewer. Please refresh and try again.
  </div>
);

const beforeCapture = (scope: Sentry.Scope, error: unknown) => {
  scope.setTag('component', 'PDFViewerLoader');
  scope.setTag('operation', 'loadPSPDFViewer');

  // A lazy-loaded viewer chunk that no longer exists (stale tab after a
  // deploy) is caught here, so it never reaches the global unhandledrejection
  // recovery. Reload once per app version instead, and drop the event while
  // the reload is in progress.
  if (recoverFromChunkLoadError(error, { appVersion: APP_VERSION })) {
    scope.addEventProcessor(() => null);
  }
};

/**
 * Error boundary for the lazily loaded PSPDFKit viewer. Reports load/render
 * failures to Sentry and shows a local error, except for stale-chunk errors
 * that trigger the one-time chunk-load recovery reload.
 */
export default function PDFViewerErrorBoundary({
  children,
}: PDFViewerErrorBoundaryProps) {
  return (
    <Sentry.ErrorBoundary
      fallback={pdfViewerErrorFallback}
      beforeCapture={beforeCapture}
    >
      {children}
    </Sentry.ErrorBoundary>
  );
}
